import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import logo from '../assets/img/Oman.png';

const backDropVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  initial: { opacity: 0, y: 30, scale: 0.96 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.35, ease: [0.22, 0.61, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    y: 20,
    scale: 0.97,
    transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
  },
};

const LoginModal = ({ isOpen, onClose, onSuccess }) => {
  const { login, loading: authLoading } = useAuth();
  const { t, isRTL } = useLanguage();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isForgotMode) return;

    setError('');
    setIsSubmitting(true);

    try {
      const result = await login(formData.email, formData.password);
      if (onSuccess) {
        onSuccess(result?.user || null);
      }
      onClose?.();
    } catch (err) {
      setError(err.message || t('website.auth.loginErrorGeneric'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotMessage('');
    setError('');

    const email = (forgotEmail || '').trim();
    if (!email) {
      setError(t('website.auth.emailRequired'));
      return;
    }

    setForgotSubmitting(true);
    try {
      const res = await fetch(`${window.location.origin.replace(/\/+$/, '')}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || t('website.auth.resetErrorGeneric'));
      }
      setForgotMessage(t('website.auth.forgotSuccess'));
    } catch (err) {
      setError(err.message || t('website.auth.resetErrorGeneric'));
    } finally {
      setForgotSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[2000] flex items-center justify-center px-4 sm:px-6"
        variants={backDropVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
        <motion.div
          variants={modalVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden"
          dir={isRTL ? 'rtl' : 'ltr'}
          onClick={(e) => e.stopPropagation()}
        >
            {/* Header with logo */}
            <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <img src={logo} alt="Logo" className="h-9 w-auto" />
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {isForgotMode ? t('website.auth.forgotTitle') : t('website.auth.loginTitle')}
                  </h2>
                  <p className="text-xs text-slate-500">
                    {isForgotMode
                      ? t('website.auth.forgotSubtitle')
                      : t('website.auth.loginSubtitle')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              {authLoading && !isForgotMode ? (
                <div className="flex flex-col items-center py-8">
                  <div className="inline-block h-10 w-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-3" />
                    <p className="text-sm text-slate-500">{t('website.auth.checkingSession')}</p>
                </div>
              ) : (
                <>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700 flex items-start gap-2"
                    >
                      <span className="mt-[2px]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                      </span>
                      <span>{error}</span>
                    </motion.div>
                  )}

                  {isForgotMode ? (
                    <form onSubmit={handleForgotSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-slate-700">
                          {t('website.auth.emailLabel')}
                        </label>
                        <input
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                          placeholder="you@example.com"
                          required
                        />
                      </div>

                      {forgotMessage && (
                        <p className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
                          {forgotMessage}
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={forgotSubmitting}
                        className="w-full inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-[#4C078C] to-[#9DD4CA] px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {forgotSubmitting
                          ? t('website.auth.sendingReset')
                          : t('website.auth.sendResetButton')}
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="space-y-4">
                        <div className="relative">
                          <label
                            htmlFor="email"
                            className={`absolute left-4 text-xs font-medium transition-all duration-200 ${
                              emailFocused || formData.email
                                ? 'top-1.5 text-primary'
                                : 'top-3 text-slate-400'
                            }`}
                          >
                            {t('website.auth.emailLabel')}
                          </label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            onFocus={() => setEmailFocused(true)}
                            onBlur={() => setEmailFocused(false)}
                            className="w-full rounded-xl border border-slate-200 px-4 pt-6 pb-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                            placeholder={emailFocused ? '' : 'admin@example.com'}
                          />
                        </div>

                        <div className="relative">
                          <label
                            htmlFor="password"
                            className={`absolute left-4 text-xs font-medium transition-all duration-200 ${
                              passwordFocused || formData.password
                                ? 'top-1.5 text-primary'
                                : 'top-3 text-slate-400'
                            }`}
                          >
                            {t('website.auth.passwordLabel')}
                          </label>
                          <input
                            id="password"
                            name="password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={formData.password}
                            onChange={handleChange}
                            onFocus={() => setPasswordFocused(true)}
                            onBlur={() => setPasswordFocused(false)}
                            className="w-full rounded-xl border border-slate-200 px-4 pt-6 pb-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                            placeholder={passwordFocused ? '' : 'Enter your password'}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs">
                        <button
                          type="button"
                          onClick={() => {
                            setIsForgotMode(true);
                            setForgotEmail(formData.email);
                            setError('');
                            setForgotMessage('');
                          }}
                          className="text-primary hover:text-primary-dark font-medium transition-colors"
                        >
                          {t('website.auth.forgotLink')}
                        </button>
                      </div>

                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                        whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                        className="w-full inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-[#4C078C] via-[#652D90] to-[#9DD4CA] px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            {t('website.auth.signingIn')}
                          </>
                        ) : (
                          t('website.auth.signInButton')
                        )}
                      </motion.button>
                    </form>
                  )}
                </>
              )}
            </div>

            {/* Footer helper */}
            <div className="px-6 pb-5 pt-3 border-t border-slate-100 bg-slate-50/60 text-[11px] text-slate-500 flex items-center justify-between">
              <span>{t('website.auth.footerNote')}</span>
              {isForgotMode && (
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotMode(false);
                    setError('');
                    setForgotMessage('');
                  }}
                  className="text-primary hover:text-primary-dark font-semibold"
                >
                  {t('website.auth.backToSignIn')}
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default LoginModal;
